import AppStore from 'AppStore'
import { AntennaButton, Box, Button, Icons, Input, ItemRow, List, Page, Spacer } from 'components'
import React, { Component } from 'react'
import { navigate } from 'shared/router'
import { showToast } from 'shared/utils'
import { T, __ } from 'translations/i18n'
import { CustomTmrItem, TmrItem, VascataItem, ItemIdentifier, BatchDecodedItems } from 'api/types'
import RfidReader from 'shared/RfidReader'
import CustomTubful from 'api/CustomTubful'
import Items from 'api/Items'
import Sounds from 'shared/Sounds'

interface State {
  data: TmrItem[]
  loading: boolean
  certilogo: string
  shipment: string
}

function sortByTubful(a, b) {
  const tubfulA = a.tubful
  const tubfulB = b.tubful

  // 1. Gestione dei valori null (Porta i valori null in fondo)
  if ((tubfulA === null || tubfulA === '') && (tubfulB === null || tubfulB === '')) {
    return 0 // Se entrambi sono null, mantengono l'ordine relativo
  }
  if (tubfulA === null || tubfulA === '') {
    return 1 // 'a' è null o vuoto, quindi 'b' (non null) viene prima -> b - a (1 lo sposta avanti)
  }
  if (tubfulB === null || tubfulB === '') {
    return -1 // 'b' è null o vuoto, quindi 'a' (non null) viene prima -> a - b (-1 lo sposta avanti)
  }

  if (typeof tubfulA !== 'string') return 1 // Considera non stringhe come "dopo" le stringhe
  if (typeof tubfulB !== 'string') return -1 // Considera non stringhe come "dopo" le stringhe

  // 2. Ordinamento alfanumerico (se nessuno dei due è null)
  // Per un ordinamento **case-insensitive** si può usare toLocaleLowerCase()
  // Utilizziamo localeCompare per un confronto stringa più robusto
  return tubfulA.localeCompare(tubfulB, 'it', { sensitivity: 'base' })
}

export default class CustomTubfulList extends Component<{}, State> {
  state: State = { data: [], loading: false, certilogo: '', shipment: '' }

  async componentDidMount() {
    if ((!AppStore.workstations || AppStore.workstations.length === 0) && !AppStore.emulation) {
      navigate('/login')
      showToast({ title: __(T.error.error), description: __(T.messages.no_workstations_found), status: 'error' })
    }
    // await RfidReader.initialize()
    // RfidReader.onDecodedItemCallback = this.onItemDecoded
  }

  renderRow = (item: TmrItem, index?: number) => {
    return (
      <ItemRow
        key={item.id}
        id={index}
        item={item}
        highlightSection="vascata"
        // onInfoPress={item.product && item.itemIdentifiers?.length > 0 ? onInfoRowPress : undefined}
        // onDeleteCallback={onDeleteRowCallback}
        // itemState={itemState}
        // hideRowParcelCode={hideRowParcelCode}
        // counter={showCounter ? items.filter((i) => i.id == item.id).length : undefined}
      />
    )
  }

  onRowClick = () => {}

  onCertilogoEnter = async (certilogo: string) => {
    this.setState({ loading: true, certilogo: '' })
    RfidReader.emulateTags([certilogo])
  }

  onItemDecoded = async (item) => {
    const { data } = this.state
    try {
      if (!item) throw new Error(__(T.misc.unknown_tag))
      if (!item.id && (item.product || item.epc)) throw new Error(__(T.error.tag_unassociated))
      if (!item.id && !item.product) throw new Error(__(T.misc.unknown_product))

      this.setState({ data: [...data, item], loading: false })
      Sounds.next()
    } catch (error) {
      showToast({ title: __(T.error.error), description: error.message, status: 'error' })
      await RfidReader.stop()
      this.setState({ loading: false })
    }
  }

  convertVascataToItem = (vascataItem: VascataItem): CustomTmrItem => {
    const currentTimestamp = +new Date()
    const itemIdentifiers: ItemIdentifier[] = [
      {
        type: 'CertilogoIdentifierDto',
        id: '',
        creationDate: currentTimestamp,
        lastModified: null,
        code: vascataItem.certilogo || '',
        description: null,
        role: null,
        item: null,
        identifierType: 'CertilogoIdentifier',
        identifierGroup: null,
        tid: null,
      },
    ]
    return {
      id: '',
      epc: '',
      upc: vascataItem.product?.code || '',
      product: {
        classType: '',
        id: '',
        code: vascataItem.product?.code || '',
        description: vascataItem.product?.description || '',
        style: {},
        size: {},
      },
      madeIn: {
        id: '',
        code: vascataItem.madeIn?.code || '',
        description: vascataItem.madeIn?.description || '',
      },
      madeFor: {
        id: '',
        code: vascataItem.madeFor?.code || '',
        description: vascataItem.madeFor?.description || '',
      },
      choice: {
        id: '',
        code: vascataItem.choice?.code || '',
        description: vascataItem.choice?.description || '',
        choiceType: vascataItem.choice?.choiceType || '',
      },
      itemIdentifiers,
      zone: { id: '', code: '', description: '' },
      productionOrderRow: {
        id: '',
        order: { id: '', code: '', description: '', classType: '', worker: null },
        product: { classType: '', id: '', code: '', description: '', style: {}, size: {} },
        quantity: 0,
        classType: '',
        rowCode: '',
        encoded: 0,
      },
      tubful: vascataItem.tubful || '',
    }
  }

  onShipmentEnter = async (shipment: string) => {
    const { data } = this.state
    try {
      this.setState({ loading: true, shipment: '' })
      const res = await CustomTubful.getTubfulByOrderCode(shipment, {
        asc: true,
        limit: 1000,
        orderBy: 'ITEM_CREATION_DATE', // || 'TUBFUL'
      })
      if (!res || !Array.isArray(res)) {
        showToast({ title: __(T.error.error), description: __(T.error.not_found), status: 'error' })
        this.setState({ loading: false })
        return
      }
      // devo fare una chiamata alla Items.batchDecode passando i certilogo ottenuti da vascata in chunk di 50
      const certilogoCodes = res.map((i) => i.certilogo).filter((c) => c && c.length > 0)
      const decodedItems: CustomTmrItem[] = []
      const chunkSize = 50
      for (let i = 0; i < certilogoCodes.length; i += chunkSize) {
        const chunk = certilogoCodes.slice(i, i + chunkSize)
        const decodedChunk = await Items.batchDecode<BatchDecodedItems>(chunk)
        decodedItems.push(...Object.values(decodedChunk))
      }

      this.setState({
        data: [...data, ...decodedItems.sort(sortByTubful)],
        loading: false,
      })
      Sounds.next()
    } catch (error) {
      showToast({ title: __(T.error.error), description: error.message, status: 'error' })
      this.setState({ loading: false })
    }
  }

  clearList = () => {
    RfidReader.clear()
    this.setState({ data: [], loading: false, certilogo: '', shipment: '' })
  }

  render() {
    const { loading, data, certilogo, shipment } = this.state
    return (
      <Page
        title={__(T.titles.tubful)}
        enableEmulation
        onBackPress={() => {
          navigate('/')
        }}
      >
        <Page.Content bgGrey>
          <Box mb={70} row style={{ alignItems: 'flex-start' }}>
            <Box width={300} bgGrey vcenter>
              <AntennaButton onItemDecoded={this.onItemDecoded} hideClear style={{ marginRight: 15 }} />
            </Box>
            <Box mt={12} row flex>
              <Input
                startFocus
                style={{ width: 300 }}
                image={<Icons.Barcode />}
                placeholder={__(T.misc.certilogo)}
                value={certilogo}
                onChange={(v) => this.setState({ certilogo: v })}
                onEnter={this.onCertilogoEnter}
              />
              <Spacer />
              <Input
                style={{ width: 300 }}
                image={<Icons.Barcode />}
                placeholder={__(T.misc.shipment)}
                value={shipment}
                onChange={(v) => this.setState({ shipment: v })}
                onEnter={this.onShipmentEnter}
              />
              <Spacer />
              <Box flex />

              <Button variant="primary" onClick={this.clearList}>
                <Icons.Trash />
              </Button>
            </Box>
          </Box>
          <List
            title={__(T.misc.tubful_list, { count: data?.length ?? 0 })}
            loading={loading}
            renderItem={this.renderRow}
            data={data}
          />
        </Page.Content>
      </Page>
    )
  }
}
